<template>
  <div class="user-select-input" ref="container">
    <!-- Preview Mode (when not focused) -->
    <div
      v-if="!isOpen"
      class="select-preview"
      :class="{ disabled: disabled }"
      @click="!disabled && openDropdown()"
      :tabindex="disabled ? -1 : 0"
      @keydown.enter="!disabled && openDropdown()"
      @keydown.space.prevent="!disabled && openDropdown()"
    >
      <div v-if="displayUser" class="preview-content">
        <span class="preview-text">{{ displayUser.displayName }}</span>
      </div>
      <span v-else class="placeholder-text">Select user</span>
    </div>

    <!-- Input Mode (when focused) -->
    <div v-else class="select-active">
      <input
        ref="searchInput"
        v-model="searchTerm"
        type="text"
        class="search-input"
        placeholder="Search users..."
        @input="handleSearch"
        @keydown.esc="closeDropdown"
        @blur="handleBlur"
      />
    </div>

    <!-- Dropdown Menu (teleported to body) -->
    <Teleport to="body">
      <div
        v-if="isOpen && dropdownPosition"
        class="dropdown-menu"
        :style="{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`
        }"
      >
        <div v-if="loading" class="dropdown-item loading-item">
          Loading...
        </div>
        <div v-else-if="users.length === 0" class="dropdown-item empty-item">
          No users found
        </div>
        <button
          v-else
          v-for="user in users"
          :key="user.userId"
          class="dropdown-item"
          @mousedown.prevent="selectUser(user)"
        >
          <span class="item-text">{{ getDisplayName(user) }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script>
import * as api from '@/api'
import { queryClient } from '@/libs/queryClient'

export default {
  name: 'UserSelectInput',
  props: {
    modelValue: {
      type: [String, Number, Array],
      default: null
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      searchTerm: '',
      users: [],
      displayUser: null,
      loading: false,
      debounceTimer: null,
      dropdownPosition: null
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.displayUser) {
          this.fetchUserForPreview(newVal)
        }
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateDropdownPosition, true)
    window.addEventListener('resize', this.updateDropdownPosition)
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer)
    window.removeEventListener('scroll', this.updateDropdownPosition, true)
    window.removeEventListener('resize', this.updateDropdownPosition)
  },
  methods: {
    async openDropdown() {
      this.isOpen = true
      this.searchTerm = ''
      this.updateDropdownPosition()
      await this.fetchUsers()
      this.$nextTick(() => {
        this.$refs.searchInput?.focus()
      })
    },
    closeDropdown() {
      this.isOpen = false
      this.searchTerm = ''
      this.users = []
      this.dropdownPosition = null
    },
    updateDropdownPosition() {
      if (!this.isOpen || !this.$refs.container) return

      const rect = this.$refs.container.getBoundingClientRect()
      const dropdownMaxHeight = 300
      const gap = 4
      const viewportHeight = window.innerHeight

      // Check if dropdown would overflow below
      const spaceBelow = viewportHeight - rect.bottom - gap
      const spaceAbove = rect.top - gap

      let top
      if (spaceBelow >= dropdownMaxHeight || spaceBelow >= spaceAbove) {
        // Position below
        top = rect.bottom + gap
      } else {
        // Position above
        top = rect.top - gap - Math.min(dropdownMaxHeight, spaceAbove)
      }

      this.dropdownPosition = {
        top,
        left: rect.left,
        width: rect.width
      }
    },
    handleBlur() {
      // Delay to allow click events to fire
      setTimeout(() => {
        this.closeDropdown()
      }, 200)
    },
    handleSearch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.fetchUsers()
      }, 300)
    },
    async fetchUsers() {
      this.loading = true
      try {
        const response = await queryClient.fetchQuery({
          queryKey: ['users', { search: this.searchTerm, limit: 50 }],
          queryFn: () => api.fetchUsers({
            search: this.searchTerm,
            limit: 50,
            offset: 0
          })
        })
        this.users = response.users || []
      } catch (error) {
        console.error('Error fetching users:', error)
        this.users = []
      } finally {
        this.loading = false
      }
    },
    async fetchUserForPreview(userId) {
      if (!userId) {
        this.displayUser = null
        return
      }

      try {
        const user = await queryClient.fetchQuery({
          queryKey: ['user', userId],
          queryFn: () => api.fetchUserById(userId)
        })
        this.displayUser = {
          ...user,
          initials: this.getInitials(user),
          displayName: this.getDisplayName(user)
        }
      } catch (error) {
        console.error('Error fetching user for preview:', error)
        this.displayUser = null
      }
    },
    selectUser(user) {
      this.$emit('update:modelValue', user.userId)
      this.displayUser = {
        ...user,
        initials: this.getInitials(user),
        displayName: this.getDisplayName(user)
      }
      this.closeDropdown()
    },
    getInitials(user) {
      const name = user.name || ''
      return name.charAt(0).toUpperCase() || 'U'
    },
    getDisplayName(user) {
      return user.name?.trim() || 'Unknown'
    }
  }
}
</script>

<style scoped>
.user-select-input {
  width: 100%;
}

/* Preview Mode Styles */
.select-preview {
  width: 100%;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #fff;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  cursor: pointer;
  height: 31px;
  display: flex;
  align-items: center;
}

.select-preview:hover {
  border-color: #9ca3af;
}

.select-preview:focus {
  outline: none;
  border-color: #4338ca;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.1);
}

.select-preview.disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
  pointer-events: none;
}

.select-preview.disabled .preview-text,
.select-preview.disabled .placeholder-text {
  color: #9ca3af;
}

.preview-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.preview-text {
  font-size: 14px;
  color: #333;
  line-height: 1;
}

.placeholder-text {
  color: #9ca3af;
  font-size: 14px;
}

/* Active Mode Styles */
.search-input {
  width: 100%;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #fff;
  border: 1px solid #4338ca;
  border-radius: var(--border-radius);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.1);
  height: 31px;
}

.search-input:focus {
  outline: none;
}

.search-input::placeholder {
  color: #9ca3af;
}

/* Dropdown Menu (Teleported) */
.dropdown-menu {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
  z-index: 10000;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s;
  font-size: 14px;
  color: #333;
}

.dropdown-item:hover {
  background-color: #f3f4f6;
}

.dropdown-item:first-child {
  border-radius: 0 8px 0 0;
}

.dropdown-item:last-child {
  border-radius: 0 0 8px 8px;
}

.loading-item,
.empty-item {
  color: #9ca3af;
  font-style: italic;
  cursor: default;
}

.loading-item:hover,
.empty-item:hover {
  background-color: transparent;
}
</style>
